import classNames from "classnames";
import { Fragment, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import type { ZypeSubscriptionPlan } from "@inferno/renderer-shared-core";
import { Button, ButtonKind } from "../../../../../core/ui";
import Heading from "../../../../../core/ui/Heading.component";
import { FORM_ERRORS, RadioInput, RadioOption } from "../../forms";
import { CoastSubscriptionIntro } from "./CoastSubscriptionIntro.components";
import { subscriptionPlanSubDescription } from "../subscriptionPlanSubDescription";
import useGeoCountry from "../../../lib/useGeoCountry";

interface CoastPickYourPlanProps {
  setIsGift: (isGift: boolean) => void;
  plans: ZypeSubscriptionPlan[];
  goForward: () => void;
  onCancel: () => void;
  stepNumber: number;
  stepCount: number;
  hidden: boolean;
  isGift: boolean;
  showCountrySelect: boolean;
}

export interface CoastPickYourPlanInputs {
  country: string;
  planId: string;
}

export const CoastPickYourPlan = ({
  setIsGift,
  goForward,
  plans,
  isGift,
  hidden,
  stepNumber,
  stepCount,
  onCancel,
}: CoastPickYourPlanProps) => {
  const { register, handleSubmit, errors, setValue } = useFormContext<CoastPickYourPlanInputs>();

  const geoCountry = useGeoCountry();

  useEffect(() => {
    if (geoCountry) {
      setValue("country", geoCountry, { shouldValidate: true });
    }
  }, [geoCountry, setValue]);

  return (
    <section className={classNames("component-pick-your-plan", "sign-up-step", { hidden })}>
      <CoastSubscriptionIntro isGift={isGift} />

      <p className="step-indicator">
        Step {stepNumber} of {stepCount}
      </p>

      <Heading level={2} className="section-title">
        Pick Your {isGift ? "Gift" : ""} Plan.
      </Heading>

      <p className="section-info">
        {isGift ? (
          <Button click={() => setIsGift(false)} className="button-info-inline" kind={ButtonKind.CUSTOMIZED}>
            Add a subscription to your account
          </Button>
        ) : (
          <Fragment>
            Change or cancel your plan anytime in "My Account" before your next billing date. &nbsp;
            <Button className="button-info-inline" click={() => setIsGift(true)} kind={ButtonKind.CUSTOMIZED}>
              Give as a gift
            </Button>
          </Fragment>
        )}
      </p>

      <form onSubmit={handleSubmit(() => goForward())}>
        <input type="hidden" name="country" ref={register({ required: FORM_ERRORS.required })} />

        <RadioInput label="Select Your Plan" error={errors.planId} direction="vertical" name="planId">
          {plans
            .filter(({ third_party_id }) => (third_party_id === "gift") === isGift)
            .map(p => (
              <RadioOption
                rules={{ required: FORM_ERRORS.required }}
                error={errors.planId}
                name="planId"
                register={register}
                label={p.description}
                subLabel={subscriptionPlanSubDescription[`${p.interval_count}_${p.interval}`] ?? ""}
                value={p._id}
                key={p._id}
              />
            ))}
        </RadioInput>

        <div className="button-prev-next">
          <Button
            disabled={!!errors.country || !!errors.planId}
            kind={ButtonKind.CUSTOMIZED}
            className="button-continue"
            type="submit"
          >
            Continue
          </Button>
          <Button kind={ButtonKind.CUSTOMIZED} className="button-previous" disabled={true}>
            Previous
          </Button>
        </div>
      </form>
      <Button className="button-cancel" click={onCancel} kind={ButtonKind.CUSTOMIZED}>
        Cancel
      </Button>
    </section>
  );
};
